import { VinDecodeResponse, VinDecodeData, Vehicle } from "../_models/vehicle";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, throwError, retry, catchError, map } from "rxjs";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { LoggingService } from "./logging.service";
import { VINDecodeCarInfoRequest, VINDecodeRequest } from "../_models/vin-decode";
import { RatesResponse } from "../_models/rates";
import brandInfo  from "../../environments/brand.json";
import { saveRatesRequest } from "../_actions/rates.actions";
import { Store } from "@ngrx/store";


@Injectable({
  providedIn: "root",
})
export class VehicleService {

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    }),
  };

  constructor(private httpClient: HttpClient, private loggingService: LoggingService, private store: Store<{}>) {}

  // Utility method to clean odometer values
  private cleanOdometerValue(odometer: any): string {
    if (odometer == null || odometer === undefined) {
      return '';
    }
    return odometer.toString().replace(/[\s,]/g, '');
  }

  // HttpClient API get() method => vin decode and fetch vehicle information
  vin_decode(vinDecodeRequest): Observable<VinDecodeData> {
    return this.httpClient
      .get<VinDecodeResponse>(
        environment.apiUrl + "dtc-vehicle-srvc/vin/decode",
        {
          params: {
            vin: vinDecodeRequest.toUpperCase(),
          },
        }
      )
      .pipe(
        map((result) => result.data),
        retry(1),
        catchError(this.loggingService.handleError)
      );
  }

  getVinCarInfo(vin: string) {
    return this.httpClient
    .get(`${environment.apiUrl}/v1/api/verifyVin/${vin}/${brandInfo.brand}`, {
    })
    .pipe(
      catchError(this.loggingService.handleError)
    );

  }

  getVehicleImage(vehicle: Vehicle) {
    return this.httpClient
      .get(`${environment.apiUrl}/v1/api/vehicle`, {
        params: {
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          trim: vehicle.trim,
          color: vehicle.exteriorColor,
        },
      })
      .pipe(
        catchError(this.loggingService.handleError)
      );
  }

  decodeVinGetRates(dataObject: VINDecodeRequest) {
    const product = this.resolveRatesProductFromSession();

    const ratesRequest = {
      vin: dataObject.vin.toUpperCase(),
      saledate: dataObject.saleDate,
      inServiceDate: dataObject.inServiceDate,
      odometer: this.cleanOdometerValue(dataObject.odometer),
      brandName: brandInfo.brand,
      customerState: dataObject.customerState,
      product: product
    };

    this.store.dispatch(saveRatesRequest(ratesRequest));

    const currentDate = new Date();
    const ratesEffectiveDate = currentDate.getFullYear() + '-' +
      String(currentDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(currentDate.getDate()).padStart(2, '0');
    sessionStorage.setItem('ratesEffectiveDate', ratesEffectiveDate);

    return this.httpClient.post<RatesResponse>(`${environment.apiUrl}/v1/api/rates/getRates`, {
      vin: dataObject.vin.toUpperCase(),
      saledate: ratesEffectiveDate,
      inServiceDate: dataObject.inServiceDate,
      odometer: this.cleanOdometerValue(dataObject.odometer),
      brandName: brandInfo.brand,
      customerState: dataObject.customerState,
      product: product
  })
  .pipe(
      map(response => {
          return {
              ...response,
              product: product
          };
      }),
      catchError(this.loggingService.handleError)
  );
  }

  /**
   * Re-fetch rates after odometer photo verification with a corrected mileage.
   * Does not dispatch loadRates (avoids modal-heavy effects). Updates ratesRequest in session.
   */
  refreshGetRatesForOdometerMileage(confirmedMiles: number) {
    const product = this.resolveRatesProductFromSession();
    const vehicle: Vehicle = JSON.parse(sessionStorage.getItem('vehicle') || '{}');
    const vin = String(vehicle?.vin || '').toUpperCase();
    const customerState = String(vehicle?.registeredState || '');

    let inServiceDate = vehicle?.inserviceDate != null ? String(vehicle.inserviceDate) : '';
    const rawReq = sessionStorage.getItem('ratesRequest');
    if (rawReq) {
      try {
        const r = JSON.parse(rawReq);
        if (r.inServiceDate) {
          inServiceDate = String(r.inServiceDate);
        }
      } catch {
        /* ignore */
      }
    }

    const currentDate = new Date();
    const ratesEffectiveDate = currentDate.getFullYear() + '-' +
      String(currentDate.getMonth() + 1).padStart(2, '0') + '-' +
      String(currentDate.getDate()).padStart(2, '0');
    sessionStorage.setItem('ratesEffectiveDate', ratesEffectiveDate);

    const body: Record<string, unknown> = {
      vin,
      saledate: ratesEffectiveDate,
      inServiceDate,
      odometer: this.cleanOdometerValue(confirmedMiles),
      brandName: brandInfo.brand,
      customerState,
      product,
    };

    if (rawReq) {
      try {
        const r = JSON.parse(rawReq);
        if (r.dealerId != null && r.dealerId !== '') {
          body['dealerId'] = r.dealerId;
        }
        if (r.language != null && r.language !== '') {
          body['language'] = r.language;
        }
      } catch {
        /* ignore */
      }
    }

    if ((brandInfo as { brand?: string }).brand === 'NCESI' && body['language'] == null) {
      body['language'] = sessionStorage.getItem('preferredLanguage') || 'en-ca';
    }

    this.store.dispatch(saveRatesRequest(body));

    return this.httpClient.post<RatesResponse>(`${environment.apiUrl}/v1/api/rates/getRates`, body).pipe(
      map((response) => ({
        ...response,
        product,
      })),
      catchError(this.loggingService.handleError)
    );
  }

  private resolveRatesProductFromSession(): string {
    const _vehicle: Vehicle = JSON.parse(sessionStorage.getItem('vehicle') || '{}');
    const page = JSON.parse(sessionStorage.getItem('pagesFlow') || 'null');

    if (page && page.landingPage === 'added-security-plan' || page && page.landingPage === 'vehicle-service-protection') {
      if (_vehicle?.isElectric) {
        return 'EVSC';
      }
      return 'VSC';
    }
    if (page && page.landingPage === 'prepaid-maintenance' || page && page.landingPage === 'pre-paid-maintenance') {
      return 'PPM';
    }
    if (page && page.landingPage === 'vehicle-protection' || page && page.landingPage === 'electric-vehicle-protection' || page && page.landingPage === 'truck-protection') {
      return 'VSC';
    }
    return 'VSC';
  }
}
